import { Controller } from "@hotwired/stimulus"

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".xls", ".xlsx", ".jpg", ".jpeg", ".png"]

type UploadJson = { success?: boolean; error?: string; message?: string }

function extensionFor(name: string): string {
  const match = name.toLowerCase().match(/(\.[a-z0-9]+)$/)
  return match?.[1] ?? ""
}

function csrfToken(): string {
  const meta = document.querySelector("meta[name='csrf-token']")
  return meta?.getAttribute("content") ?? ""
}

async function parseJson(response: Response): Promise<UploadJson | null> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

export default class extends Controller {
  static targets = ["form", "input", "dropzone", "error", "status"]
  static values = {
    maxBytes: { type: Number, default: 15 * 1024 * 1024 },
    maxFiles: { type: Number, default: 10 },
    currentCount: { type: Number, default: 0 },
  }

  declare formTarget: HTMLFormElement
  declare inputTarget: HTMLInputElement
  declare dropzoneTarget: HTMLElement
  declare errorTarget: HTMLElement
  declare statusTarget: HTMLElement
  declare maxBytesValue: number
  declare maxFilesValue: number
  declare currentCountValue: number

  dragover(event: DragEvent) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-ag-orange", "bg-ag-light/40")
  }

  dragleave(event: DragEvent) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-ag-orange", "bg-ag-light/40")
  }

  drop(event: DragEvent) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-ag-orange", "bg-ag-light/40")
    const file = event.dataTransfer?.files?.[0]
    if (file) {
      this.uploadFile(file)
    }
  }

  fileChosen() {
    const file = this.inputTarget.files?.[0]
    if (file) {
      this.uploadFile(file)
    }
  }

  async uploadFile(file: File) {
    this.clearError()

    if (this.currentCountValue >= this.maxFilesValue) {
      this.showError(`You can upload a maximum of ${this.maxFilesValue} documents.`)
      this.resetInput()
      return
    }

    const extension = extensionFor(file.name)
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      this.showError("File type not allowed. Allowed: PDF, DOCX, XLS, XLSX, JPEG, PNG.")
      this.resetInput()
      return
    }

    if (file.size > this.maxBytesValue) {
      this.showError("File exceeds the maximum size of 15MB.")
      this.resetInput()
      return
    }

    this.statusTarget.textContent = `Uploading ${file.name}…`

    const token = csrfToken()
    const formData = new FormData()
    formData.append("document", file)
    formData.append("authenticity_token", token)

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": token,
        },
        credentials: "same-origin",
      })

      if (response.status === 413) {
        this.showError("Upload rejected by the server as too large.")
        this.resetInput()
        return
      }

      const data = await parseJson(response)

      if (!response.ok || data?.success === false) {
        this.showError(data?.error || "Could not upload document. Please try again.")
        this.resetInput()
        return
      }

      window.location.reload()
    } catch {
      this.showError("Could not upload document. Please try again.")
      this.resetInput()
    }
  }

  showError(message: string) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
    this.statusTarget.textContent = ""
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  resetInput() {
    this.inputTarget.value = ""
    this.statusTarget.textContent = ""
  }
}
